import React, {useCallback, useState} from "react";
import Button from "../../../base/button/button";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import ReactCreditCards from "../../../../custom/react-credit-cards";
import {getCardMonth} from "../../../../../core/services/utils/utils";
import {CustomerPaymentMethodTypeNames} from "../../../../../core/constants/enums";
import {ReactComponent as Chevron} from "../../../../../assets/images/chevron/chevron-down.svg";
import {ReactComponent as SeparatorLine} from "../../../../../assets/images/card-more-option-separator.svg";
import classnames from "classnames";
import {Link} from "react-router-dom";
import {Collapse} from "@mui/material";
import ChargeCreditCardDialog from "../../../dialogs/charge-credit-card-dialog";
import useAccessLevels from "../../../../hooks/use-access-levels";

const initChargeCreditCardDialogState = {open: false, card: null, customerId: null, close: () => void 0};

const CreditCard = ({
                        withWrapper = false,
                        card,
                        remove,
                        setDefault,
                        customerId,
                    }) => {
    const operatorAccessLevels = useAccessLevels();
    const [chargeCreditCardDialogState, setChargeCreditCardDialogState] = useState(initChargeCreditCardDialogState);
    const [isExtended, setIsExtended] = useState(false);

    /**
     * Invokes the setDefault callback if the card is not already default.
     * @type {(function(*): void)}
     */
    const onSetDefault = useCallback((e) => {
        e.stopPropagation();
        if (card.isDefault)
            return;
        setDefault(card);
    }, [card, setDefault]);

    /**
     * Opens the charge credit card dialog.
     * @type {(function(): void)}
     */
    const onCharge = useCallback(() => {
        setChargeCreditCardDialogState({
            open: true,
            card: card,
            customerId: customerId,
            close: () => setChargeCreditCardDialogState(initChargeCreditCardDialogState)
        })
    }, [card, customerId]);

    /**
     * Gets users confirmation before removing the current entity.
     */
    const getRemoveConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to remove the selected credit card?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && remove().then(),
        );
    }, [remove]);

    return (
        <>
            {
                !withWrapper &&
                <div className={classnames({'card-with-mask': card.isDefault})}>
                    <ReactCreditCards
                        preview
                        cvc={'***'}
                        expiry={`${getCardMonth(card?.month)}`}
                        focused={'name'}
                        name={card.isDefault ? 'Default Card' : ' '}
                        number={`************${card?.lastFourDigits}`}
                        issuer={CustomerPaymentMethodTypeNames[card?.type] ?? 'unknown'}
                    />
                </div>
            }
            {
                withWrapper &&
                <div
                    className={classnames('card-wrapper', {
                        'card-with-mask': card.isDefault,
                        "has-background-image": !!card?.fileName
                    })}
                    style={{'--background-image-url': `url(${card?.fileName})` ?? ''}}
                >
                    <div className={card.isDefault ? 'position-relative' : ''}>
                        <ReactCreditCards
                            preview
                            cvc={'***'}
                            expiry={`${getCardMonth(card?.month)}`}
                            focused={'name'}
                            name={card.isDefault ? 'Default Card' : ' '}
                            number={`************${card?.lastFourDigits}`}
                            issuer={CustomerPaymentMethodTypeNames[card?.type] ?? 'unknown'}
                        />
                    </div>
                    <div className={'options-section'}>
                        {
                            operatorAccessLevels.packages.sales.chargeCreditCards &&
                            <Button
                                className='py-1'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={onCharge}
                            >
                                Charge
                            </Button>
                        }
                        <div/>
                        <div
                            className={classnames('card-more-options', {'extended': isExtended})}
                            onClick={() => setIsExtended(prevState => !prevState)}
                        >
                            <p>More Options</p>
                            <Chevron className={'mt-1'}/>
                        </div>
                    </div>
                    <Collapse in={isExtended}>
                        <div className={'expanded-section-separator'}>
                            <SeparatorLine/>
                        </div>
                        {
                            customerId &&
                            operatorAccessLevels.packages.sales.paymentMethodsTransactionHistory &&
                            <Button
                                className='py-2 w-100 mb-1'
                                variant={'outlined'}
                                color={"primary"}
                                component={Link}
                                to={routeFunctions.customer.cardSummary.creditCards(customerId, card.id)}
                            >
                                View Summary
                            </Button>
                        }
                        {
                            (!card.isDefault && typeof setDefault === 'function') &&
                            <Button
                                className='py-2 w-100 my-1'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={onSetDefault}
                            >
                                Set Default
                            </Button>
                        }
                        {
                            !!remove &&
                            <Button
                                className='p-2 w-100 mb-1'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={getRemoveConfirmation}
                            >
                                Remove
                            </Button>
                        }
                    </Collapse>
                </div>
            }
            <ChargeCreditCardDialog
                open={chargeCreditCardDialogState.open}
                setOpen={chargeCreditCardDialogState.close}
                customerId={chargeCreditCardDialogState.customerId}
                card={chargeCreditCardDialogState.card}
            />
        </>
    );
}

export default CreditCard;
